import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

import Button from '@components/Button';
import { TextField } from '@components/FormFields';
import CustomSpinner from '@components/Spinner/CustomSpinner';
import { useUserCreate } from '@hooks';
import { RAILS_ENV, RECAPTCHA_SITE_KEY } from '@src/env';
import { LeadType } from '@utils/backend-api/users';
import { splitInitialDob } from '@utils/dates';
import { extractQueryParams } from '@utils/queryStringHelper';
import { YOUNG_PROFESSIONALS_PLAN } from '@utils/subscriptionCardOptions';

import { SubscriptionFormProps } from '.';
import DateOfBirthField from './DateOfBirthField';
import styles from './styles.module.scss';
import validationSchema from './validationSchema';

interface FormValues {
  day: { label: number; value: number };
  dob: string;
  email: string;
  month: { label: string; value: number };
  name: string;
  userEmail?: string;
  year: { label: number; value: number };
}

interface SignupFormProps extends SubscriptionFormProps {
  initialDob?: string;
  setStripeCustomerId: (stripeCustomerId: string) => void;
  setDob?: (dob: string) => void;
  formLocation: string;
}

const SignupForm = ({
  dob,
  formLocation,
  initialDob,
  referringSubscriberGuid,
  selectedOffer: { code },
  setStripeCustomerId,
  setDob,
  userFullName,
  userEmail,
}: SignupFormProps) => {
  const [disableNameAndEmailInputs, setDisableNameAndEmailInputs] = useState(
    !!userFullName && !!userEmail
  );
  const [disableDobInput, setDisableDobInput] = useState(true);
  const { mutateAsync: createUser, isLoading } = useUserCreate();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const formRef = useRef<FormikProps<FormValues>>();
  const { article_id: referringArticleId } = extractQueryParams(['article_id']);
  const { initialDay, initialMonth, initialYear } = splitInitialDob(initialDob);
  const isYPPlan = code.includes(YOUNG_PROFESSIONALS_PLAN);
  const leadType = referringSubscriberGuid ? LeadType.REFERRAL_PAGE : LeadType.SUBSCRIBE_PAGE;

  // Passed userEmail to the form for email validation initial state
  const initialState = {
    day: initialDay,
    dob: '',
    email: userEmail || '',
    month: initialMonth,
    name: userFullName || '',
    userEmail,
    year: initialYear,
  };

  useEffect(() => {
    if (isYPPlan && !dob) {
      setDisableDobInput(false);
    } else {
      setDisableDobInput(true);
    }
  }, [isYPPlan, dob]);

  const handleCreateUser = async ({ day, email, month, name, year }: FormValues) => {
    try {
      // Send an empty string for reCaptcha token in test environment so it doesn't fail
      const refToken = RAILS_ENV === 'test' ? '' : await reCaptchaRef.current.executeAsync();
      const dobString = isYPPlan ? `${year.value}-${month.value}-${day.value}` : null;
      const createUserParams = {
        dob: dobString,
        email,
        lead_type: leadType,
        form_location: formLocation,
        page: window.location.pathname,
        name,
        code,
        recaptcha_token: refToken,
        referring_article_id: referringArticleId,
      };
      const { stripeCustomerId } = await createUser(createUserParams);
      setDisableNameAndEmailInputs(true);
      setDisableDobInput(true);
      setStripeCustomerId(stripeCustomerId);
      setDob(dobString);
      reCaptchaRef.current.reset();
    } catch {
      setDisableNameAndEmailInputs(false);
      setDisableDobInput(false);
      reCaptchaRef.current.reset();
    }
  };

  return (
    <div className={styles.subscriptionForm}>
      <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
        <Formik
          enableReinitialize
          initialValues={initialState}
          onSubmit={handleCreateUser}
          validationSchema={validationSchema(isYPPlan)}
          innerRef={formRef}
        >
          {({ handleSubmit, errors, touched }) => (
            <Form className="account-info" noValidate onSubmit={handleSubmit}>
              <div className="mb-20 mt-24 flex items-center">
                <div className="font-sans text-body-sm font-bold uppercase text-primary-red">
                  {userEmail ? 'Your account' : 'Create your account'}
                </div>
                <div className="ml-12 h-2 flex-1 bg-primary-red"></div>
              </div>
              <TextField
                name="name"
                placeholder="Full Name"
                disabled={!!userFullName || disableNameAndEmailInputs}
              />
              <TextField
                name="email"
                placeholder="Email"
                disabled={!!userEmail || disableNameAndEmailInputs}
              />
              {isYPPlan && (
                <DateOfBirthField
                  disableDobInput={disableDobInput}
                  error={
                    touched.month && touched.day && touched.year && errors?.dob ? errors.dob : null
                  }
                />
              )}
              <Button
                id="signupBtn"
                disabled={isLoading || (disableNameAndEmailInputs && disableDobInput)}
                type="submit"
                className={clsx(styles.submitBtn, 'mt-16')}
              >
                {isLoading ? (
                  <CustomSpinner
                    color="#fff"
                    size={10}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  />
                ) : (
                  <>Continue</>
                )}
              </Button>
            </Form>
          )}
        </Formik>
      </ReCAPTCHA>
    </div>
  );
};

export default SignupForm;
